import { create } from 'zustand';
import { ClientUserDTO, CreateClientUserRequestDTO, UpdateClientUserRequestDTO } from '@api/generated/search-service';
import { clientUserManagementApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface ClientUserFilters {
  search?: string;
  page?: number;
  size?: number;
}

interface UserState {
  users: ClientUserDTO[];
  isLoading: boolean;
  error: string | null;
  selectedUser: ClientUserDTO | null;
  isModalOpen: boolean;
  isEditMode: boolean;

  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;

  // Actions
  fetchUsers: (filters?: ClientUserFilters) => Promise<void>;
  createUser: (userData: CreateClientUserRequestDTO) => Promise<boolean>;
  updateUser: (userId: number, userData: UpdateClientUserRequestDTO) => Promise<boolean>;
  deleteUser: (userId: number) => Promise<boolean>;
  openCreateModal: () => void;
  openEditModal: (user: ClientUserDTO) => void;
  closeModal: () => void;
  clearError: () => void;
}

export const useUserStore = create<UserState>(set => ({
  users: [],
  isLoading: false,
  error: null,
  selectedUser: null,
  isModalOpen: false,
  isEditMode: false,

  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrev: false,

  fetchUsers: async (filters?: ClientUserFilters) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        set({ error: 'Organizace nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      const page = filters?.page || 1;
      const size = filters?.size || 20;

      const response = await clientUserManagementApi.getClientUsers({
        organizationId,
        portalId,
        page: page - 1, // API uses 0-based indexing
        size,
        search: filters?.search,
      });

      set({
        users: response.data?.users || [],
        total: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0,
        currentPage: page,
        pageSize: size,
        hasNext: page < (response.data?.totalPages || 0),
        hasPrev: page > 1,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst uživatele';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  createUser: async (userData: CreateClientUserRequestDTO) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        set({ error: 'Organizace nebo portál nebyl nalezen', isLoading: false });
        return false;
      }

      const response = await clientUserManagementApi.createClientUser({
        organizationId,
        portalId,
        createClientUserRequestDTO: userData,
      });

      // Přidáme nového uživatele do seznamu
      set(state => ({
        users: [...state.users, response.data],
        isLoading: false,
        error: null,
        isModalOpen: false,
        selectedUser: null,
        isEditMode: false,
      }));

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se vytvořit uživatele';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  updateUser: async (userId: number, userData: UpdateClientUserRequestDTO) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        set({ error: 'Organizace nebo portál nebyl nalezen', isLoading: false });
        return false;
      }

      const response = await clientUserManagementApi.updateClientUser({
        organizationId,
        portalId,
        userId,
        updateClientUserRequestDTO: userData,
      });

      // Aktualizujeme uživatele v seznamu
      set(state => ({
        users: state.users.map(user => (user.id === userId ? response.data : user)),
        isLoading: false,
        error: null,
        isModalOpen: false,
        selectedUser: null,
        isEditMode: false,
      }));

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se aktualizovat uživatele';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  deleteUser: async (userId: number) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        set({ error: 'Organizace nebo portál nebyl nalezen', isLoading: false });
        return false;
      }

      await clientUserManagementApi.deleteClientUser({
        organizationId,
        portalId,
        userId,
      });

      // Odebereme uživatele ze seznamu
      set(state => ({
        users: state.users.filter(user => user.id !== userId),
        isLoading: false,
        error: null,
      }));

      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se smazat uživatele';
      set({
        error: errorMessage,
        isLoading: false,
      });
      return false;
    }
  },

  openCreateModal: () => {
    set({
      isModalOpen: true,
      isEditMode: false,
      selectedUser: null,
      error: null,
    });
  },

  openEditModal: (user: ClientUserDTO) => {
    set({
      isModalOpen: true,
      isEditMode: true,
      selectedUser: user,
      error: null,
    });
  },

  closeModal: () => {
    set({
      isModalOpen: false,
      isEditMode: false,
      selectedUser: null,
      error: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));
